import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Form, FormProps, Input, message, Select, SelectProps } from 'antd';
import {
  ITransformActiveIndices,
  ITransformItemActiveIndices,
  useDataTransformPanelStore,
} from '../store';
import TransformRuleBuilder from '@components/data-transform-panel/components/TransformRuleBuilder';
import {
  getEmptyTransformRuleGroup,
  getEmptyTransformRuleItem,
} from '@components/data-transform-panel/utils/initializeUtils';
import { trackClickWorkflowSave, trackDimensionOperation } from '@src/sensor';
import { EInputType } from '@components/mapping-panel/components/RuleBuilder/KeyValueBuilder/typed';
import { ITransformData } from '@src/common/types/empTypes';
import { useDimensionPanelStore } from '@components/dimensions/store';
import {
  useDeleteDataTransform,
  usePostDataTransform,
  usePresetList,
  useUpdateDataTransform,
} from '@api/emp/queryHooks';
import { validateTransformData } from '@components/data-transform-panel/utils/validateUtils';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { GroupTitle } from '@src/emp-3.0/components/Dimensions/DimensionLegacy/CustomDimension';
import { validateMetricName } from 'emp-3.0/components/Metrics/IndividualMetric/MetricDetail';
import { ETransformRuleActionType } from '@components/data-transform-panel/typed';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import {
  addAndTransformMetadataItem,
  addOrTransformMetadataItem,
  addTransformMetadataGroup,
  deleteTransformMetadataGroup,
  deleteTransformMetadataItem,
  trimTransformRules,
  updateTransformMetadataByLogicOperator,
  updateTransformRules,
} from '@components/data-transform-panel/utils/handleTransformRulesUtils';
import {
  EDimensionType,
  ERenamedDimension,
  ERenamePreset,
  ERenameTitle,
} from '@src/common/constant/enum';
import { renamePresetDimensionMap } from '@src/common/constant/emp';
import { ISaveCallback } from '@src/pages/workflow/types';
import { useNavigate } from 'react-router-dom';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import { StyledFormError } from '@src/pages/workflow/components/BuilderWrapper/styled';

interface IRenameDimensionProps {
  formProps: FormProps;
  onValuesChange?: (changedValues: any, allValues: any) => void;
  setServerSideError?: (v) => void;
}
export const handleSuccess = (operation: string) => {
  trackDimensionOperation({ type: operation, status: 'success' });
  message.success(`Dimension ${operation === 'edit' ? 'Updated' : 'Created'} Successfully!`);
};
export const titlePresetMap = {
  [ERenameTitle.PAGE]: ERenamePreset.PAGE,
  [ERenameTitle.SCREEN]: ERenamePreset.SCREEN,
};
export const titleOptions = [
  {
    value: ERenameTitle.PAGE,
    label: ERenameTitle.PAGE,
  },
  {
    value: ERenameTitle.SCREEN,
    label: ERenameTitle.SCREEN,
  },
];
export const saveDimensionOptions = [
  { value: ERenamedDimension.edited_page_title, label: 'Edited Page Title' },
  { value: ERenamedDimension.edited_screen_title, label: 'Edited Screen Title' },
];
export const handleError = (operation: string) => (error: any) => {
  trackDimensionOperation({ type: operation, status: 'fail' });
  const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
  message.error(errorMessage);
};

const titleTargetDimensionMap = {
  [ERenameTitle.PAGE]: renamePresetDimensionMap[ERenamePreset.PAGE],
  [ERenameTitle.SCREEN]: renamePresetDimensionMap[ERenamePreset.SCREEN],
};

const RenameDimension = forwardRef<any, IRenameDimensionProps>((props, ref) => {
  const { formProps, onValuesChange, setServerSideError } = props;
  const { form } = formProps;

  const { status, enableRequired } = useFieldRequiredStatus(['transform_name']);

  const navigate = useNavigate();

  const { data: presetList } = usePresetList();
  const {
    transformActiveIndices,
    transformId,
    transformTitle,
    transformTargetDimension,
    transformRule,
    setLoadingStates,
    setTransformActiveIndices,
    setTransformTitle,
    setTransformRule,
    resetTransformStore,
  } = useDataTransformPanelStore((state) => state);
  const [setCurPresetName] = useDimensionPanelStore((state) => [state.setCurPresetName]);
  const { mutateAsync: createDataTransform, isLoading: isCreateLoading } = usePostDataTransform();
  const { mutateAsync: updateDataTransform, isLoading: isUpdateLoading } = useUpdateDataTransform();
  const { mutate: deleteDataTransform, isLoading: isDeleteLoading } = useDeleteDataTransform();

  const [validation, setValidation] = useState<{ failed: boolean; field: string }>({
    failed: false,
    field: '',
  });

  const isUpdateMode = !!transformId;
  const [setNeedRefetchQueryKeys] = useMappingPanelStore((state) => [
    state.setNeedRefetchQueryKeys,
  ]);

  useEffect(() => {
    setLoadingStates({
      isCreateLoading,
      isUpdateLoading,
      isDeleteLoading,
    });
  }, [isCreateLoading, isDeleteLoading, isUpdateLoading, setLoadingStates]);

  const modifyNeedRefetchQueryFlag = () => {
    transformTargetDimension && setNeedRefetchQueryKeys(transformTargetDimension);
  };
  const handleCreateDataTransform = (res: any, cb?: ISaveCallback, innercb?: () => void) => {
    createDataTransform(res.transformData, {
      onSuccess: (data) => {
        message.success('create success!');
        modifyNeedRefetchQueryFlag();

        const url = location.pathname.replace('new', data?.data.id);
        navigate(url, { replace: true });
        innercb?.();
        cb?.onSuccess?.();
        if (innercb) {
        } else {
          trackClickWorkflowSave({ type: 'rename', operation: 'create', id: data?.data.id });
        }
      },
      onError: (error: any) => {
        if (setServerSideError) {
          setServerSideError(error?.response?.data?.msg);
        } else {
          message.error(error?.response?.data?.msg);
        }
        cb?.onError?.();
        const errorMsg = error?.response?.data?.msg || 'unknown error';
        if (innercb) {
        } else {
          trackClickWorkflowSave({ type: 'rename', operation: 'create', error: errorMsg });
        }
      },
      onSettled: () => {
        cb?.onSettled?.();
      },
    });
  };
  const handleUpdateDataTransform = (res: any, cb?: ISaveCallback, innercb?: () => void) => {
    updateDataTransform(
      { nameId: res.nameId, queryBody: res.transformData },
      {
        onSuccess: () => {
          message.success('update success!');
          modifyNeedRefetchQueryFlag();
          innercb?.();
          cb?.onSuccess?.();
          if (innercb) {
          } else {
            trackClickWorkflowSave({ type: 'rename', operation: 'edit', id: res.nameId });
          }
        },
        onError: (error: any) => {
          if (setServerSideError) {
            setServerSideError(error?.response?.data?.msg);
          } else {
            message.error(error?.response?.data?.msg);
          }
          cb?.onError?.();
          const errorMsg = error?.response?.data?.msg || 'unknown error';
          if (innercb) {
          } else {
            trackClickWorkflowSave({
              type: 'rename',
              operation: 'edit',
              id: res.nameId,
              error: errorMsg,
            });
          }
        },
        onSettled: () => {
          cb?.onSettled?.();
        },
      },
    );
  };
  const handleDeleteDataTransform = (res: any, cb?: ISaveCallback) => {
    deleteDataTransform(
      { nameId: res.nameId },
      {
        onSuccess: () => {
          message.success('Delete success!');
          resetTransformStore();
          navigate(-1);
          modifyNeedRefetchQueryFlag();
        },
        onError: (error: any) => {
          if (setServerSideError) {
            setServerSideError(error?.response?.data?.msg);
          } else {
            message.error(error?.response?.data?.msg);
          }
        },
        onSettled: () => {
          cb?.onSettled?.();
        },
      },
    );
  };

  const handleSave = (cb?: ISaveCallback, innercb?: () => void) => {
    const uncheckTransformData = {
      dimension_value: form?.getFieldValue('transform_name'),
      target_dimension: form?.getFieldValue('target_dimension'),
      transform_rule: [...trimTransformRules(transformRule)],
    };
    const res = validateTransformData(uncheckTransformData as ITransformData);
    if (!res.status) {
      if (res.field === 'dimension_value') {
        message.warning(`${res.key?.join(' OR ')} ${res.result}`).then();
      }
      setValidation({ failed: true, field: res.field });
      return;
    }
    isUpdateMode
      ? handleUpdateDataTransform({ nameId: transformId, ...res }, cb, innercb)
      : handleCreateDataTransform(res, cb, innercb);
  };

  useImperativeHandle(ref, () => ({
    handleSave(cb?: ISaveCallback, innercb?: () => void) {
      handleSave(cb, innercb);
    },
    handleDelete(cb?: ISaveCallback) {
      handleDeleteDataTransform({ nameId: transformId }, cb);
    },
  }));
  const updateTransformActiveIndices = (
    orIndex?: number,
    metadataNameActive?: boolean,
    ruleItems?: ITransformItemActiveIndices[],
  ) => {
    setTransformActiveIndices({ orIndex, metadataNameActive, ruleItems });
  };
  const handleChange = (
    orIndex: number,
    subAndIndex: number,
    subOrIndex: number,
    type: EInputType,
    value: string,
  ) => {
    let updatedTransformRule = [...transformRule];

    if (type === EInputType.METADATA_NAME) {
      updatedTransformRule[orIndex].source_dimension = value;
      updatedTransformRule[orIndex].rule = [[getEmptyTransformRuleItem()]];
      if (validation.field === 'metaName') {
        setValidation({ failed: false, field: '' });
      }
    } else {
      const updatedRules = updateTransformRules(
        updatedTransformRule,
        orIndex,
        subAndIndex,
        subOrIndex,
        type,
        value,
      );
      updatedTransformRule = [...updatedRules];

      if (type === EInputType.METADATA_VALUE && validation.field === 'rule') {
        setValidation({ failed: false, field: '' });
      }
    }

    setTransformRule(updatedTransformRule);
    setServerSideError?.('');
  };

  const handleRulesChange = (
    actionType: ETransformRuleActionType,
    orIndex = 0,
    subAndIndex = 0,
    subOrIndex?: number,
  ) => {
    let rule = transformRule;
    switch (actionType) {
      case ETransformRuleActionType.DELETE_ALL_TRANSFORM:
        rule = [getEmptyTransformRuleGroup()];
        break;
      case ETransformRuleActionType.DELETE_TRANSFORM_METADATA_GROUP:
        rule = [...deleteTransformMetadataGroup(rule, orIndex)];
        break;
      case ETransformRuleActionType.DELETE_TRANSFORM_METADATA_ITEM:
        rule = [...deleteTransformMetadataItem(rule, orIndex, subAndIndex, subOrIndex ?? 0)];
        break;
      case ETransformRuleActionType.ADD_TRANSFORM_METADATA_GROUP:
        rule = [...addTransformMetadataGroup(rule)];
        break;
      case ETransformRuleActionType.ADD_AND_TRANSFORM_METADATA_ITEM:
        rule = [...addAndTransformMetadataItem(rule, orIndex)];
        break;
      case ETransformRuleActionType.ADD_OR_TRANSFORM_METADATA_ITEM:
        rule = [...addOrTransformMetadataItem(rule, orIndex)];
        break;
      case ETransformRuleActionType.LOGIC_OPERATOR_CHANGE:
        rule = [...updateTransformMetadataByLogicOperator(rule, orIndex, subAndIndex, subOrIndex)];
        break;
      default:
        break;
    }
    setTransformRule(rule);
    setServerSideError?.('');
  };
  const handleDragEnd = (result) => {
    const { destination, source } = result;

    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const rules = transformRule;

    try {
      const destinationObj = JSON.parse(destination.droppableId);
      const sourceObj = JSON.parse(source.droppableId);
      const { orIndex, subAndIndex: destinationSubAndIndex } = destinationObj;
      const { subAndIndex: sourceSubAndIndex } = sourceObj;
      const sourceIndex = source.index;
      const destinationIndex = destination.index;

      const temp = rules[orIndex].rule[sourceSubAndIndex].splice(sourceIndex, 1);
      rules[orIndex].rule[destinationSubAndIndex].splice(destinationIndex, 0, ...temp);

      setTransformRule([...rules]);
      setServerSideError?.('');

      const activeIndicesProps: ITransformActiveIndices = {
        orIndex,
        ruleItems: [
          {
            subAndIndex: destinationSubAndIndex,
            subOrIndex: destinationIndex,
          },
        ],
      };

      setTransformActiveIndices(activeIndicesProps);
    } catch (e) {
      console.log('Drag drop end error: ', e);
    }
  };
  const metadataNameOptions = useMemo(() => {
    let options: SelectProps['options'] = [];

    if (transformTitle && presetList?.data) {
      (presetList?.data ?? []).forEach((dataSource) => {
        if (dataSource.preset_name === titlePresetMap[transformTitle]) {
          options = (dataSource.dimension_list ?? [])
            ?.filter((dimension) => dimension.dimension_type === EDimensionType.EVENT_ATTRIBUTE)
            ?.map((dimension) => ({
              value: dimension.dimension_name,
              label: dimension.display_name,
            }));
        }
      });
    }
    return options;
  }, [presetList, transformTitle]);
  const handleTitleChange = (value) => {
    setTransformTitle(value);
    setCurPresetName(titlePresetMap[value]);
    form?.setFieldValue('target_dimension', titleTargetDimensionMap[value]);
    setTransformRule([getEmptyTransformRuleGroup()]);
    setServerSideError?.('');
  };
  return (
    <div style={{ margin: '24px' }} ref={ref}>
      <Form form={form} {...formProps} onValuesChange={onValuesChange}>
        <CustomFormItem
          name="transform_title"
          label="Data Transform"
          required
          rules={[{ validator: (rule, value) => validateMetricName(rule, value, 'dimension') }]}
          labelStyle={{ minWidth: 140, textAlign: 'left' }}
        >
          <Select
            disabled={isUpdateMode}
            style={{ width: '100%' }}
            onChange={handleTitleChange}
            options={titleOptions}
          />
        </CustomFormItem>
        <CustomFormItem
          name="target_dimension"
          label="Save to Dimension"
          required
          labelStyle={{ minWidth: 140, textAlign: 'left' }}
        >
          <Select disabled style={{ width: '100%' }} options={saveDimensionOptions} />
        </CustomFormItem>
        <CustomFormItem
          name="transform_name"
          label="Set New Value"
          required
          validateStatus={status}
          rules={[
            {
              validator: (rule, value) =>
                validateMetricName(rule, value, 'new value', enableRequired),
            },
          ]}
          labelStyle={{ minWidth: 140, textAlign: 'left' }}
        >
          <Input placeholder="Please input..." />
        </CustomFormItem>

        <GroupTitle>Transformation Rules</GroupTitle>
        {validation.failed && (
          <StyledFormError style={{ marginBottom: 5 }}>
            * Required fields in red are missing.
          </StyledFormError>
        )}
        <TransformRuleBuilder
          errorField={validation.field}
          activeIndices={transformActiveIndices}
          data={transformRule}
          updateActiveIndices={updateTransformActiveIndices}
          onChange={handleChange}
          onRulesChange={handleRulesChange}
          onDragEnd={handleDragEnd}
          metadataList={metadataNameOptions}
          noDeleteAll
        />
      </Form>
    </div>
  );
});
RenameDimension.displayName = 'RenameDimension';
export default RenameDimension;
